import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { CARD_CLASS, QUICK_ACTIONS } from "../../constants/dashboard";
import { useHasPermission } from "../../store/hooks/useRbac";
import { PERMISSIONS } from "../../utils/rbac/permissions";

const QuickActions: React.FC = () => {
  const navigate = useNavigate();

  const canApplyLeave = useHasPermission([
    PERMISSIONS.APPLY_LEAVE_VIEW,
    PERMISSIONS.APPLY_LEAVE_MANAGE,
  ]);
  const canRegularize = useHasPermission([
    PERMISSIONS.APPLY_AR_VIEW,
    PERMISSIONS.APPLY_AR_MANAGE,
  ]);
  const canFillTimesheet = useHasPermission([
    PERMISSIONS.MY_TIMESHEETS_VIEW,
    PERMISSIONS.MY_TIMESHEETS_MANAGE,
  ]);
  const canViewProfile = useHasPermission([
    PERMISSIONS.EMPLOYEE_PROFILE_VIEW,
    PERMISSIONS.EMPLOYEE_PROFILE_MANAGE,
  ]);

  const allowedActions = useMemo(() => {
    return QUICK_ACTIONS.filter((action) => {
      switch (action.key) {
        case "apply-leave":
          return canApplyLeave;
        case "regularize":
          return canRegularize;
        case "timesheet":
          return canFillTimesheet;
        case "documents":
          return canViewProfile;
        default:
          return true;
      }
    });
  }, [canApplyLeave, canRegularize, canFillTimesheet, canViewProfile]);

  if (allowedActions.length === 0) {
    return null;
  }

  return (
    <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-${Math.min(allowedActions.length, 4)} gap-3 sm:gap-4 md:gap-5`}>
      {allowedActions.map((action) => (
        <button
          key={action.key}
          type="button"
          onClick={() => navigate(action.path)}
          className={`${CARD_CLASS} p-4 sm:p-5 flex flex-col items-start gap-3.5 cursor-pointer text-left hover:-translate-y-1 hover:shadow-md transition-all duration-200 focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none`}
        >
          <span
            className="w-11 h-11 rounded-xl grid place-items-center flex-none"
            style={{ background: action.iconBg }}
          >
            <action.Icon
              className="w-5 h-5"
              style={{ color: action.iconColor }}
            />
          </span>
          <span className="w-full flex items-center justify-between gap-2">
            <span className="text-body-md font-bold text-text-primary">
              {action.label}
            </span>
            <ArrowRight className="w-4 h-4 text-text-tertiary" />
          </span>
        </button>
      ))}
    </div>
  );
};

export default QuickActions;
